import { useState, useEffect, useRef } from 'react';

export default function Statistics() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  const stats = [
    {
      icon: 'ri-shopping-bag-3-line',
      number: 5000,
      suffix: '+',
      label: 'قطعة فاخرة',
      description: 'تم اختيارها بعناية'
    },
    {
      icon: 'ri-user-heart-line',
      number: 2500,
      suffix: '+',
      label: 'عميلة سعيدة',
      description: 'ثقة وتجربة مميزة'
    },
    {
      icon: 'ri-star-line',
      number: 98,
      suffix: '%',
      label: 'رضا العملاء',
      description: 'تقييمات إيجابية'
    },
    {
      icon: 'ri-global-line',
      number: 15,
      suffix: '+',
      label: 'دولة',
      description: 'نصل إليها'
    }
  ];

  const [counts, setCounts] = useState(stats.map(() => 0));

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isVisible) return;

    const duration = 2000;
    const steps = 60;
    const stepDuration = duration / steps;

    stats.forEach((stat, index) => {
      let currentStep = 0;
      const increment = stat.number / steps;

      const timer = setInterval(() => {
        currentStep++;
        setCounts(prev => {
          const newCounts = [...prev];
          newCounts[index] = Math.min(Math.round(increment * currentStep), stat.number);
          return newCounts;
        });

        if (currentStep >= steps) {
          clearInterval(timer);
        }
      }, stepDuration);
    });
  }, [isVisible]);

  return (
    <section ref={sectionRef} className="py-20 px-6 bg-gradient-to-b from-white to-[#F8F6F0]">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h2 
            className={`text-4xl md:text-5xl font-bold text-[#C9A88A] mb-4 transition-all duration-1000 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            أرقامنا تتحدث
          </h2>
          <div 
            className={`w-24 h-1 bg-[#D4AF7A] mx-auto transition-all duration-1000 delay-200 ${
              isVisible ? 'opacity-100 scale-x-100' : 'opacity-0 scale-x-0'
            }`}
          ></div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {stats.map((stat, index) => (
            <div
              key={index}
              className={`text-center p-8 rounded-2xl bg-white shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
              }`}
              style={{ transitionDelay: `${index * 150}ms` }}
            >
              <div className="w-20 h-20 flex items-center justify-center mx-auto mb-6 bg-gradient-to-br from-[#D4AF7A] to-[#C9A88A] rounded-full shadow-lg transform transition-transform duration-300 hover:scale-110 hover:rotate-12">
                <i className={`${stat.icon} text-4xl text-white`}></i>
              </div>
              <div className="mb-2">
                <span className="text-5xl font-bold text-[#D4AF7A]">
                  {counts[index]}
                </span>
                <span className="text-3xl font-bold text-[#D4AF7A]">
                  {stat.suffix}
                </span>
              </div>
              <h3 className="text-xl font-bold text-[#3D3D3D] mb-2">
                {stat.label}
              </h3>
              <p className="text-[#3D3D3D]/70 text-sm">
                {stat.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
